import type { EngagementEmailProps } from "./types";

const FONT = "Arial, Helvetica, sans-serif";

function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function statTile(value: string | number, label: string, color: string): string {
  return `
  <td valign="top" width="33%" align="center" style="padding:6px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border:1px solid #E5E7EB;border-radius:8px;">
      <tr><td align="center" style="padding:18px 8px;">
        <div style="font-family:${FONT};color:${color};font-size:28px;font-weight:bold;line-height:32px;">${esc(value)}</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">${esc(label)}</div>
      </td></tr>
    </table>
  </td>`;
}

function bullet(text: string): string {
  return `
  <tr>
    <td width="16" valign="top" style="font-family:${FONT};color:#D4A843;font-size:14px;line-height:22px;">&bull;</td>
    <td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:22px;padding:0 0 6px 4px;">${esc(text)}</td>
  </tr>`;
}

function sourceCard(icon: string, title: string, desc: string): string {
  return `
  <td valign="top" width="33%" style="padding:6px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFFFFF;border:1px solid #E5E7EB;border-radius:8px;">
      <tr><td align="center" style="padding:14px;">
        <div style="font-family:${FONT};font-size:24px;line-height:28px;">${icon}</div>
        <div style="font-family:${FONT};color:#1F3864;font-size:13px;font-weight:bold;line-height:18px;margin-top:8px;">${esc(title)}</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:12px;line-height:18px;margin-top:4px;">${esc(desc)}</div>
      </td></tr>
    </table>
  </td>`;
}

function variantHotLead(p: EngagementEmailProps, isGroup: boolean): string {
  const score = p.hotLeadScore ?? 0;
  const pillSpec = isGroup ? `&#128100; ${esc(p.hotLeadSpecialty ?? "")}` : `&#127973; ${esc(p.hotLeadSpecialty ?? "")}`;
  const desc = isGroup
    ? `This inquiry scored ${esc(score)} out of 100 based on your locality, waiver type match, and urgency level. Placement coordinators move quickly &mdash; follow up today to secure this placement.`
    : `This lead scored ${esc(score)} out of 100 based on your Golden ZIP codes, specialty match, and urgency level. Families in crisis contact multiple agencies &mdash; the first to respond usually wins the client.`;
  const tip = isGroup
    ? " Respond within 24 hours. Support coordinators often contact multiple programs &mdash; early responses build the relationship."
    : " Call within the first hour. Studies show lead conversion rates drop by over 80% after the first hour of contact.";

  return `
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FEF2F2;border:2px solid #FECACA;border-left:6px solid #DC2626;border-radius:8px;">
    <tr><td style="padding:24px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td valign="top" width="70%">
            <div style="font-family:${FONT};color:#DC2626;font-size:11px;font-weight:bold;letter-spacing:1.5px;line-height:16px;">HOT LEAD &mdash; SCORE ${esc(score)}/100</div>
            <div style="font-family:${FONT};color:#1F3864;font-size:22px;font-weight:bold;line-height:28px;margin-top:6px;">${esc(p.hotLeadName ?? "")}</div>
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin-top:10px;">
              <tr>
                <td style="background-color:#FFFFFF;border:1px solid #E5E7EB;border-radius:4px;padding:4px 10px;font-family:${FONT};font-size:12px;color:#374151;">&#128205; ${esc(p.hotLeadZip ?? "")}</td>
                <td width="6" style="font-size:1px;line-height:1px;">&nbsp;</td>
                <td style="background-color:#FFFFFF;border:1px solid #E5E7EB;border-radius:4px;padding:4px 10px;font-family:${FONT};font-size:12px;color:#374151;">${pillSpec}</td>
              </tr>
            </table>
            <div style="font-family:${FONT};color:#6B7280;font-size:13px;line-height:20px;margin-top:12px;">${desc}</div>
          </td>
          <td valign="middle" align="center" width="30%">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="80" height="80" style="background-color:#DC2626;border-radius:40px;">
              <tr><td align="center" valign="middle" style="text-align:center;line-height:80px;height:80px;width:80px;">
                <div style="font-family:${FONT};color:#FFFFFF;font-size:28px;font-weight:bold;line-height:32px;">${esc(score)}</div>
                <div style="font-family:${FONT};color:#FECACA;font-size:13px;line-height:16px;">/ 100</div>
              </td></tr>
            </table>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>

  <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:20px auto 0;">
    <tr>
      <td align="center" style="background-color:#D4A843;border-radius:6px;">
        <a href="${esc(p.bookingUrl)}" style="display:inline-block;padding:12px 22px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;">Book a Discovery Call &rarr;</a>
      </td>
      <td width="10" style="font-size:1px;line-height:1px;">&nbsp;</td>
      <td align="center" style="background-color:#FFFFFF;border:2px solid #1F3864;border-radius:6px;">
        <a href="${esc(p.dashboardUrl)}" style="display:inline-block;padding:10px 20px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;">View Full Lead Details &rarr;</a>
      </td>
    </tr>
  </table>

  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFFBF0;border-left:4px solid #D4A843;border-radius:0 6px 6px 0;margin-top:20px;">
    <tr><td style="padding:12px 16px;font-family:${FONT};color:#374151;font-size:13px;line-height:20px;">
      <span style="color:#9A7A1A;font-weight:bold;">&#128161; Pro tip:</span>${tip}
    </td></tr>
  </table>`;
}

function variantNoHotLead(p: EngagementEmailProps, isGroup: boolean): string {
  const tile1Label = isGroup ? "Inquiries Received" : "Leads Captured";
  const tile3Label = isGroup ? "Urgent Inquiries So Far" : "Hot Leads So Far";

  return `
  <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:16px;">Where you stand after ${esc(p.totalLeadsCaptured)} ${esc(p.segment_label)}</div>
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
    <tr>
      ${statTile(p.totalLeadsCaptured, tile1Label, "#1F3864")}
      ${statTile(p.avgScore, "Average Score", "#2E75B6")}
      ${statTile(0, tile3Label, "#DC2626")}
    </tr>
  </table>

  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F8F9FB;border:1px solid #E5E7EB;border-radius:8px;margin-top:20px;">
    <tr><td style="padding:24px;">
      <div style="font-family:${FONT};color:#1F3864;font-size:15px;font-weight:bold;line-height:20px;margin-bottom:12px;">Why haven&#39;t you seen a hot lead yet?</div>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
        ${bullet("Your chatbot may not be embedded yet — leads can only be scored if they come through the intake form")}
        ${bullet("Your Golden ZIP codes or priority specialties may not be configured — these add up to 25 bonus points per lead")}
        ${bullet("Your service area may need more ZIP codes — a lead can only score the ZIP match bonus if their ZIP is in your list")}
      </table>
    </td></tr>
  </table>

  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#FFFBF0;border:2px solid #D4A843;border-radius:8px;margin-top:20px;">
    <tr><td style="padding:20px;">
      <div style="font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;line-height:20px;margin-bottom:10px;">The fastest way to your first hot lead:</div>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr><td width="20" valign="top" style="font-family:${FONT};color:#D4A843;font-size:14px;font-weight:bold;line-height:20px;">1.</td><td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:20px;padding-bottom:6px;">Go to Lead Filters and mark your top 2 ZIP codes as Golden Zones &mdash; this adds 15 bonus points per matched lead.</td></tr>
        <tr><td width="20" valign="top" style="font-family:${FONT};color:#D4A843;font-size:14px;font-weight:bold;line-height:20px;">2.</td><td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:20px;padding-bottom:6px;">Mark your highest-priority specialties as Priority &mdash; this adds 10 bonus points per specialty match.</td></tr>
        <tr><td width="20" valign="top" style="font-family:${FONT};color:#D4A843;font-size:14px;font-weight:bold;line-height:20px;">3.</td><td valign="top" style="font-family:${FONT};color:#374151;font-size:14px;line-height:20px;">Embed your chatbot on your website if you haven&#39;t already &mdash; every unembedded lead is a missed scoring opportunity.</td></tr>
      </table>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:16px auto 0;">
        <tr><td align="center" style="background-color:#D4A843;border-radius:6px;">
          <a href="${esc(p.dashboardUrl)}/filters" style="display:inline-block;padding:12px 22px;font-family:${FONT};color:#1F3864;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;">Configure My Lead Filters &rarr;</a>
        </td></tr>
      </table>
    </td></tr>
  </table>`;
}

export function generateEngagementEmailHTML(p: EngagementEmailProps): string {
  const isGroup = p.segment === "grouphome";

  const headline = p.hasHotLead
    ? `&#128293; You have a hot lead, ${esc(p.ownerName)}.`
    : `Your ${esc(p.segment_label)} are coming in, ${esc(p.ownerName)}.`;
  const subhead = p.hasHotLead
    ? `A lead just scored ${esc(p.hotLeadScore ?? 0)}/100. This one is worth your time right now.`
    : `You have captured ${esc(p.totalLeadsCaptured)} ${esc(p.segment_label)} so far. Here is how to convert them.`;
  const preheader = p.hasHotLead
    ? `Score: ${p.hotLeadScore ?? 0}/100. This lead needs your attention today.`
    : `You have captured ${p.totalLeadsCaptured} leads. Here is how to turn them into clients.`;

  const body = p.hasHotLead ? variantHotLead(p, isGroup) : variantNoHotLead(p, isGroup);

  const sourceCards = isGroup
    ? `<tr>
        ${sourceCard("&#128100;", "Support Coordinator", "Primary referral source for 68% of group home placements")}
        ${sourceCard("&#127973;", "Hospital Social Worker", "Refers individuals who need community residential placement")}
        ${sourceCard("&#129309;", "Community Services Board", "Mission-aligned — connects individuals to licensed programs")}
       </tr>`
    : `<tr>
        ${sourceCard("&#127973;", "Hospital Discharge Planner", "Highest volume referral source for home care agencies")}
        ${sourceCard("&#128105;&#8205;&#9877;&#65039;", "Physical Therapist", "Refers patients who need continued support at home")}
        ${sourceCard("&#127960;", "Area Agency on Aging", "Mission-aligned — refers clients to trusted providers")}
       </tr>`;

  const referralBody = isGroup
    ? "Inbound inquiries are just one source. Programs with full beds build strong relationships with support coordinators, MCO care managers, and CSBs. LegacyGrowth Engine has 10 personalized email templates ready to send &mdash; it takes 60 seconds."
    : "Inbound leads are just one source. The agencies that scale fastest build strong referral relationships with hospital discharge planners, case managers, and physicians. LegacyGrowth Engine has 14 personalized email templates ready to send &mdash; it takes 60 seconds.";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>Your first hot lead is waiting</title>
</head>
<body style="margin:0;padding:0;background-color:#F0F2F5;font-family:${FONT};">
<div style="display:none;max-height:0;overflow:hidden;color:transparent;visibility:hidden;mso-hide:all;font-size:1px;line-height:1px;">${esc(preheader)}</div>
<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#F0F2F5;">
  <tr><td align="center" style="padding:24px 12px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;background-color:#FFFFFF;border-radius:8px;">

      <tr><td style="background-color:#0F1E35;padding:32px 40px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td valign="middle" style="font-family:${FONT};color:#D4A843;font-size:18px;font-weight:bold;line-height:24px;">LegacyGrowth Engine</td>
            <td valign="middle" align="right">
              <span style="background-color:#1F3864;color:#D4A843;font-family:${FONT};font-size:11px;font-weight:bold;padding:4px 10px;border-radius:4px;display:inline-block;">${esc(p.plan)} Plan &mdash; Free Trial</span>
            </td>
          </tr>
        </table>
        <div style="border-top:1px solid #2E3D55;margin:18px 0 16px;"></div>
        <div style="font-family:${FONT};color:#FFFFFF;font-size:26px;font-weight:bold;line-height:32px;text-align:center;">${headline}</div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:15px;line-height:24px;text-align:center;margin-top:8px;">${subhead}</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:18px auto 0;">
          <tr><td align="center" style="background-color:#1F3864;border-radius:6px;padding:12px 20px;font-family:${FONT};color:#D4A843;font-size:13px;font-weight:bold;text-align:center;line-height:18px;">
            &#9201;&nbsp; ${esc(p.trialDaysRemaining)} days left in your free trial &mdash; expires ${esc(p.trialEndDate)}
          </td></tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#FFFFFF;padding:32px 40px;">${body}</td></tr>

      <tr><td style="background-color:#F8F9FB;border-top:1px solid #E5E7EB;padding:28px 40px;">
        <div style="font-family:${FONT};color:#1F3864;font-size:16px;font-weight:bold;line-height:22px;margin-bottom:12px;">While you wait &mdash; build your referral pipeline</div>
        <div style="font-family:${FONT};color:#6B7280;font-size:14px;line-height:22px;margin-bottom:16px;">${referralBody}</div>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${sourceCards}</table>
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:18px auto 0;">
          <tr><td align="center" style="background-color:#1F3864;border-radius:6px;">
            <a href="${esc(p.referralOutreachUrl)}" style="display:inline-block;padding:13px 24px;font-family:${FONT};color:#FFFFFF;font-size:14px;font-weight:bold;text-decoration:none;line-height:20px;">Send My First Referral Email &rarr;</a>
          </td></tr>
        </table>
      </td></tr>

      <tr><td style="background-color:#0F1E35;padding:24px 40px;">
        <div style="font-family:${FONT};line-height:20px;">
          <span style="color:#D4A843;font-size:14px;font-weight:bold;">LegacyGrowth Engine</span><span style="color:#9AAFC8;font-size:14px;"> &mdash; A product of H&amp;E Associates LLC</span>
        </div>
        <div style="font-family:${FONT};color:#9AAFC8;font-size:12px;line-height:20px;margin-top:6px;">McLean, Virginia &nbsp;&middot;&nbsp; ${esc(p.supportEmail)}</div>
        <div style="margin-top:10px;font-family:${FONT};font-size:12px;line-height:18px;">
          <a href="mailto:${esc(p.supportEmail)}" style="color:#D4A843;text-decoration:underline;">Contact Support</a>
          <span style="color:#9AAFC8;"> &middot; </span>
          <a href="${esc(p.unsubscribeUrl)}" style="color:#9AAFC8;text-decoration:underline;">Unsubscribe</a>
        </div>
        <div style="font-family:${FONT};color:#4B5563;font-size:11px;line-height:18px;margin-top:12px;">You are receiving this email because you are an active LegacyGrowth Engine subscriber with ${esc(p.trialDaysRemaining)} days remaining in your free trial. To manage your notification preferences, visit your account settings.</div>
      </td></tr>

    </table>
  </td></tr>
</table>
</body>
</html>`;
}
